import React from 'react';

import { Box, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';

import { newDate } from '~/services/TimeService';

import {
  DATE_FORMAT,
  displayToPercent,
  formatCurrency,
  formatPercentDisplay,
  percentToDisplay,
} from './CharterFlightsPricingHelpers';
import type { CharterPricingRow } from './CharterFlightsPricingTypes';

// Shared configuration for percentage columns: converts decimal to whole number for editing
function createPercentColumnConfig(field: keyof CharterPricingRow) {
  return {
    type: 'number' as const,
    editable: true,
    valueFormatter: formatPercentDisplay,
    valueGetter: percentToDisplay,
    valueSetter: (value: number, row: CharterPricingRow) => ({
      ...row,
      [field]: displayToPercent(value),
    }),
  };
}

export const charterFlightsPricingColumns: Array<GridColDef<CharterPricingRow>> = [
  {
    field: 'direction',
    headerName: 'Direction',
    width: 100,
    editable: false,
  },
  {
    field: 'departureDate',
    headerName: 'Departure Date',
    width: 140,
    editable: false,
    sortComparator: (v1: string, v2: string) => {
      const date1 = newDate(v1, DATE_FORMAT);
      const date2 = newDate(v2, DATE_FORMAT);
      return date1.valueOf() - date2.valueOf();
    },
  },
  {
    field: 'offeredForSale',
    headerName: 'On Sale',
    width: 100,
    type: 'boolean',
    editable: false,
    sortable: true,
    filterable: true,
  },
  {
    field: 'packagedAdultEconomyPrice',
    headerName: 'Price',
    width: 100,
    type: 'number',
    editable: true,
    valueFormatter: formatCurrency,
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Price
        </Typography>
      </Box>
    ),
  },
  {
    field: 'packagedAdultEconomyFree',
    headerName: 'Free',
    width: 70,
    type: 'boolean',
    editable: false,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderHeader: () => (
      <Tooltip title="Overrides bundled economy adult price to $0">
        <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
          <Typography variant="body2" fontWeight={500}>
            Free
          </Typography>
        </Box>
      </Tooltip>
    ),
  },
  {
    field: 'packagedAdultSkyLoungeUpsell',
    headerName: 'Economy Extra',
    width: 120,
    type: 'number',
    editable: true,
    valueFormatter: formatCurrency,
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Economy Extra
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Upsell
        </Typography>
      </Box>
    ),
  },
  {
    field: 'packagedAdultPremiumEconomyUpsell',
    headerName: 'Premium Economy',
    width: 140,
    type: 'number',
    editable: true,
    valueFormatter: formatCurrency,
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Premium Economy
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Upsell
        </Typography>
      </Box>
    ),
  },
  {
    field: 'packagedAdultLuxRowPaxOneUpsell',
    headerName: 'Economy Extra for One',
    width: 170,
    type: 'number',
    editable: true,
    valueFormatter: formatCurrency,
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Economy Extra for One
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Upsell
        </Typography>
      </Box>
    ),
  },
  {
    field: 'packagedAdultBusinessPrice',
    headerName: 'Business',
    width: 100,
    type: 'number',
    editable: true,
    valueFormatter: formatCurrency,
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Business
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Price
        </Typography>
      </Box>
    ),
  },
  {
    field: 'packagedChildEconomyMultiplier',
    headerName: '% of Adult',
    width: 130,
    ...createPercentColumnConfig('packagedChildEconomyMultiplier'),
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          % of Adult
        </Typography>
      </Box>
    ),
  },
  {
    field: 'packagedChildEconomyFree',
    headerName: 'Free',
    width: 60,
    type: 'boolean',
    editable: false,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderHeader: () => (
      <Tooltip title="Kids Fly Free — overrides bundled economy child price to $0">
        <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
          <Typography variant="body2" fontWeight={500}>
            Free
          </Typography>
        </Box>
      </Tooltip>
    ),
  },
  {
    field: 'packagedChildBusinessMultiplier',
    headerName: 'Business',
    width: 90,
    ...createPercentColumnConfig('packagedChildBusinessMultiplier'),
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Business
        </Typography>
        <Typography variant="caption" color="text.secondary">
          % of Adult
        </Typography>
      </Box>
    ),
  },
  {
    field: 'packagedInfantEconomyMultiplier',
    headerName: 'Economy (All)',
    width: 130,
    ...createPercentColumnConfig('packagedInfantEconomyMultiplier'),
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Economy (All)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          % of Adult
        </Typography>
      </Box>
    ),
  },
  {
    field: 'packagedInfantBusinessMultiplier',
    headerName: 'Business',
    width: 90,
    ...createPercentColumnConfig('packagedInfantBusinessMultiplier'),
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Business
        </Typography>
        <Typography variant="caption" color="text.secondary">
          % of Adult
        </Typography>
      </Box>
    ),
  },
  {
    field: 'separator',
    headerName: '',
    width: 10,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: () => null,
  },
  {
    field: 'standaloneReturnBusinessMarkup',
    headerName: 'Business',
    width: 130,
    ...createPercentColumnConfig('standaloneReturnBusinessMarkup'),
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Business
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
          % markup on Packaged Price
        </Typography>
      </Box>
    ),
  },
  {
    field: 'standaloneReturnEconomyMarkup',
    headerName: 'Economy (All)',
    width: 130,
    ...createPercentColumnConfig('standaloneReturnEconomyMarkup'),
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Economy (All)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
          % markup on Packaged Price
        </Typography>
      </Box>
    ),
  },
  {
    field: 'standaloneOneWayBusinessMarkup',
    headerName: 'Business',
    width: 130,
    ...createPercentColumnConfig('standaloneOneWayBusinessMarkup'),
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Business
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
          % markup on Packaged Price
        </Typography>
      </Box>
    ),
  },
  {
    field: 'standaloneOneWayEconomyMarkup',
    headerName: 'Economy (All)',
    minWidth: 130,
    flex: 1,
    ...createPercentColumnConfig('standaloneOneWayEconomyMarkup'),
    renderHeader: () => (
      <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
        <Typography variant="body2" fontWeight={500}>
          Economy (All)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
          % markup on Packaged Price
        </Typography>
      </Box>
    ),
  },
];

export const charterFlightsPricingColumnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'flightInfo',
    headerName: '',
    children: [{ field: 'direction' }, { field: 'departureDate' }, { field: 'offeredForSale' }],
  },
  {
    groupId: 'packagedFares',
    headerName: 'Packaged Fares',
    children: [
      {
        groupId: 'adult',
        headerName: 'Adult',
        children: [
          {
            groupId: 'adultEconomy',
            headerName: 'Economy',
            children: [{ field: 'packagedAdultEconomyPrice' }, { field: 'packagedAdultEconomyFree' }],
          },
          { field: 'packagedAdultSkyLoungeUpsell' },
          { field: 'packagedAdultPremiumEconomyUpsell' },
          { field: 'packagedAdultLuxRowPaxOneUpsell' },
          { field: 'packagedAdultBusinessPrice' },
        ],
      },
      {
        groupId: 'child',
        headerName: 'Child',
        children: [
          {
            groupId: 'childEconomy',
            headerName: 'Economy',
            children: [{ field: 'packagedChildEconomyMultiplier' }, { field: 'packagedChildEconomyFree' }],
          },
          { field: 'packagedChildBusinessMultiplier' },
        ],
      },
      {
        groupId: 'infant',
        headerName: 'Infant',
        children: [{ field: 'packagedInfantEconomyMultiplier' }, { field: 'packagedInfantBusinessMultiplier' }],
      },
    ],
  },
  {
    groupId: 'separatorGroup',
    headerName: '',
    children: [{ field: 'separator' }],
  },
  {
    groupId: 'standaloneReturn',
    headerName: 'Standalone Return',
    children: [{ field: 'standaloneReturnBusinessMarkup' }, { field: 'standaloneReturnEconomyMarkup' }],
  },
  {
    groupId: 'standaloneOneWay',
    headerName: 'Standalone One-Way',
    children: [{ field: 'standaloneOneWayBusinessMarkup' }, { field: 'standaloneOneWayEconomyMarkup' }],
  },
];
